import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Calendar, ChevronLeft, ChevronRight, MessageCircle, Pencil, Plus } from "lucide-react";

import { AdminTopbar } from "@/components/admin/AdminTopbar";
import {
  AdminCard,
  FilterChip,
  ListState,
  StatCard,
  StatusPill,
} from "@/components/admin/primitives";
import { useDebounced } from "@/hooks/use-debounced";
import { formatDate, monthLabel } from "@/lib/dates";
import { countLabel, formatBRL, padOrderNumber } from "@/lib/format";
import type { OrderStatus } from "@/lib/orders";
import { ORDER_STATUSES, ORDER_STATUS_LABEL, isNewOrder, whatsappUrl } from "@/lib/orders";
import { cn } from "@/lib/utils";
import { useAdminOrders, useOrdersSummary } from "@/lib/queries";

export const Route = createFileRoute("/admin/_painel/pedidos/")({
  head: () => ({ meta: [{ title: "Pedidos — Painel MonteBox" }] }),
  component: AdminPedidos,
});

type StatusFilter = OrderStatus | "todos";

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "todos", label: "Todos" },
  ...ORDER_STATUSES.map((value) => ({ value, label: ORDER_STATUS_LABEL[value] })),
];

const PER_PAGE = 8;

/** As seis colunas da tabela, repetidas no cabecalho e em cada linha. */
const COLUMNS = "min-w-[1000px] grid grid-cols-[150px_300px_120px_150px_140px_1fr] items-center gap-4";

function AdminPedidos() {
  const [status, setStatus] = useState<StatusFilter>("todos");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  // A busca so vai para a API depois que a digitacao para.
  const search = useDebounced(query, 300);

  const filter = useMemo(
    () => ({
      status: status === "todos" ? undefined : status,
      q: search || undefined,
      page,
      perPage: PER_PAGE,
    }),
    [status, search, page],
  );

  const orders = useAdminOrders(filter);
  const summary = useOrdersSummary();

  const items = orders.data?.items ?? [];
  const total = orders.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const period = summary.data ? monthLabel(summary.data.period) : "";

  return (
    <>
      <AdminTopbar
        title="Pedidos"
        search={{
          placeholder: "Buscar por número, cliente ou WhatsApp",
          value: query,
          onChange: (value) => {
            setQuery(value);
            // Buscar de novo a partir da pagina 3 esconderia os resultados.
            setPage(1);
          },
        }}
        actions={
          <Link
            to="/"
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-mb-navy px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-mb-ink"
          >
            <Plus className="h-4 w-4 stroke-[2.5]" />
            Novo pedido
          </Link>
        }
      />

      <div className="flex flex-col gap-6 p-4 sm:p-8">
        <div className="grid gap-4 sm:grid-cols-3 sm:gap-6">
          <StatCard
            label={period ? `Pedidos em ${period}` : "Pedidos no mês"}
            value={summary.data ? String(summary.data.ordersInPeriod) : "—"}
          />
          <StatCard
            label="Novos pedidos (não abertos)"
            value={summary.data ? String(summary.data.newOrders) : "—"}
          />
          <StatCard
            label={period ? `Valor em pedidos · ${period}` : "Valor no mês"}
            value={summary.data ? formatBRL(summary.data.valueInPeriod) : "—"}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((item) => (
            <FilterChip
              key={item.value}
              active={status === item.value}
              onClick={() => {
                setStatus(item.value);
                setPage(1);
              }}
            >
              {item.label}
            </FilterChip>
          ))}
          <span className="ml-auto flex items-center gap-2 rounded-full border border-mb-line bg-white px-3.5 py-2 text-xs font-bold text-mb-muted first-letter:uppercase">
            <Calendar className="h-3.5 w-3.5" />
            {period || "Mês atual"}
          </span>
        </div>

        <AdminCard className="overflow-x-auto">
          <div className={`${COLUMNS} border-b border-mb-line bg-mb-surface px-6 py-3`}>
            {["Pedido", "Cliente", "Peças", "Total", "Status"].map((head) => (
              <span
                key={head}
                className="text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted"
              >
                {head}
              </span>
            ))}
            <span className="text-right text-[11px] font-bold uppercase leading-4 tracking-[0.1em] text-mb-muted">
              Ações
            </span>
          </div>

          <ListState
            loading={orders.isLoading}
            error={orders.error}
            empty={items.length === 0}
            emptyLabel="Nenhum pedido encontrado com esse filtro."
          />

          {items.map((order) => {
            const pieces = order.items.reduce((sum, item) => sum + item.quantity, 0);
            // Enviado pelo cliente e ainda não aberto: é o que a equipe precisa
            // pegar primeiro, então a linha se destaca até alguém abrir.
            const fresh = isNewOrder(order);

            return (
              <div
                key={order.id}
                className={cn(
                  COLUMNS,
                  "border-b border-mb-line px-6 py-3.5 last:border-b-0",
                  fresh && "bg-mb-yellow/10 shadow-[inset_3px_0_0] shadow-mb-yellow",
                )}
              >
                <div>
                  <p className="flex items-center gap-2 font-display text-sm font-bold tabular-nums text-mb-navy">
                    #{padOrderNumber(order.number)}
                    {fresh && (
                      <span className="rounded-full bg-mb-yellow px-2 py-0.5 font-sans text-[10px] font-bold uppercase leading-3 tracking-wide text-mb-ink">
                        Novo
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] leading-4 text-mb-muted">
                    {formatDate(order.createdAt)}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-mb-ink">{order.customer.name}</p>
                  <p className="truncate text-[11px] leading-4 text-mb-muted">
                    {order.customer.whatsapp}
                    {order.customer.email ? ` · ${order.customer.email}` : ""}
                  </p>
                </div>
                <span className="text-sm tabular-nums text-mb-muted">{pieces}</span>
                <span className="text-sm font-bold tabular-nums text-mb-navy">
                  {formatBRL(order.total)}
                </span>
                <StatusPill status={order.status} className="justify-self-start" />

                <div className="flex items-center justify-self-end gap-1.5">
                  <a
                    href={whatsappUrl(order)}
                    target="_blank"
                    rel="noreferrer"
                    title={`Chamar ${order.customer.name} no WhatsApp`}
                    aria-label={`Chamar ${order.customer.name} no WhatsApp`}
                    className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                  >
                    <MessageCircle className="h-[15px] w-[15px]" />
                  </a>
                  <Link
                    to="/admin/pedidos/$pedidoId"
                    params={{ pedidoId: order.id }}
                    title="Abrir o pedido"
                    aria-label={`Abrir o pedido ${padOrderNumber(order.number)}`}
                    className="grid h-8 w-8 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                  >
                    <Pencil className="h-[15px] w-[15px]" />
                  </Link>
                  <Link
                    to="/admin/pedidos/$pedidoId"
                    params={{ pedidoId: order.id }}
                    className="flex items-center gap-1.5 rounded-full border border-mb-line px-3.5 py-[7px] text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
                  >
                    Abrir
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            );
          })}
        </AdminCard>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-mb-muted">
            Mostrando {items.length} de {countLabel(total, "pedido", "pedidos")}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              aria-label="Página anterior"
              disabled={page === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="grid h-8 w-8 place-items-center rounded-full border border-mb-line bg-white text-mb-muted transition-colors hover:text-mb-navy disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: pages }, (_, index) => index + 1).map((number) => (
              <button
                key={number}
                type="button"
                onClick={() => setPage(number)}
                aria-current={number === page ? "page" : undefined}
                className={
                  number === page
                    ? "grid h-8 w-8 place-items-center rounded-full bg-mb-navy text-xs font-bold text-white"
                    : "grid h-8 w-8 place-items-center rounded-full border border-mb-line bg-white text-xs font-bold text-mb-muted transition-colors hover:text-mb-navy"
                }
              >
                {number}
              </button>
            ))}
            <button
              type="button"
              aria-label="Próxima página"
              disabled={page >= pages}
              onClick={() => setPage((current) => Math.min(pages, current + 1))}
              className="grid h-8 w-8 place-items-center rounded-full border border-mb-line bg-white text-mb-muted transition-colors hover:text-mb-navy disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
